import { SKILL_LABEL, INTENT_LABEL, type UserCard } from '@shared/types';
import { isEn, t } from './i18n';

/**
 * `1 post`, `2 posts`.
 *
 * Thai has no plural, so every count in this app was written as `{n} คน` and read correctly whatever n
 * was. English does not get that for free, and "1 reviews" under somebody's first review is exactly the
 * sort of seam that makes a page feel machine-translated.
 */
export const plural = (n: number, one: string, many = one + 's'): string =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

export function fmtDistance(km: number | undefined | null): string {
  if (km == null || !isFinite(km)) return '';
  // Built strings never match a dictionary key, so the few units that end up inside one are asked here.
  const [m, k] = isEn() ? [' m', ' km'] : [' ม.', ' กม.'];
  if (km < 1) return `${Math.round(km * 1000)}${m}`;
  if (km < 10) return `${km.toFixed(1)}${k}`;
  return `${Math.round(km)}${k}`;
}

export function fmtBaht(n: number | null | undefined): string {
  if (n == null) return '';
  if (n === 0) return isEn() ? 'Free' : 'ฟรี';
  return `฿${n.toLocaleString('th-TH')}`;
}

export function fmtPriceRange(min: number | null, max: number | null, unit: 'court_hour' | 'person' | 'free' = 'court_hour'): string {
  if (unit === 'free') return isEn() ? 'Free' : 'ฟรี';
  if (min == null && max == null) return isEn() ? 'Price not listed' : 'ไม่ระบุราคา';
  const suffix = unit === 'person' ? (isEn() ? '/person' : '/คน') : isEn() ? '/hr' : '/ชม.';
  if (min != null && max != null && min !== max) return `฿${min.toLocaleString()}–${max.toLocaleString()}${suffix}`;
  return `฿${(min ?? max)!.toLocaleString()}${suffix}`;
}

/*
 * These two read the dictionary themselves rather than leaving it to the vnode hook, because their
 * answer is usually glued into a longer string first — `กลาง–โปร`, `ชิลล์ · ทุกระดับ` — and a built
 * string matches no key. Asking here means the words are English wherever they end up.
 */
export function skillLabel(level: number | null | undefined): string {
  if (level == null) return '';
  return t(SKILL_LABEL[level] ?? '');
}

export function intentLabel(intent: string | null | undefined): string {
  if (!intent) return '';
  return t(INTENT_LABEL[intent] ?? intent);
}

export function skillRange(min: number | null, max: number | null): string {
  if (min == null && max == null) return isEn() ? 'All levels' : 'ทุกระดับ';
  if (min != null && max != null) return min === max ? skillLabel(min) : `${skillLabel(min)}–${skillLabel(max)}`;
  return min != null ? `${skillLabel(min)}+` : isEn() ? `up to ${skillLabel(max)}` : `ถึง ${skillLabel(max)}`;
}

export function initials(name: string): string {
  const t = name.trim();
  if (!t) return '?';
  // Thai has no case; take first grapheme-ish char(s)
  const parts = t.split(/\s+/);
  if (parts.length >= 2 && /^[A-Za-z]/.test(parts[0]) && /^[A-Za-z]/.test(parts[1])) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return Array.from(t).slice(0, /^[A-Za-z]/.test(t) ? 2 : 1).join('').toUpperCase();
}

export function userName(u: Pick<UserCard, 'display_name' | 'handle'>): string {
  return u.display_name || `@${u.handle}`;
}

export function mapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

export function pluralTh(n: number, unit: string): string {
  return `${n.toLocaleString('th-TH')} ${unit}`;
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}
